'use client';

import type { BxFormBrand } from '../eas';
import {
  type InlineFormBrandConfig,
  type InlineFormProps,
  InlineForm as SharedInlineForm,
} from './InlineForm';
import {
  type ModalFormBrandConfig,
  type ModalFormProps,
  ModalForm as SharedModalForm,
} from './ModalForm';
import {
  type MultiFieldInlineFormBrandConfig,
  type MultiFieldInlineFormProps,
  MultiFieldInlineForm as SharedMultiFieldInlineForm,
} from './MultiFieldInlineForm';
import {
  VendorClaimProfileForm as SharedVendorClaimProfileForm,
  type VendorClaimProfileFormBrandConfig,
  type VendorClaimProfileFormProps,
} from './VendorClaimProfileForm';
import { ComparisonChartIcon } from './components/ComparisonChartIcon';

export const INLINE_FORM_BRAND_CONFIGS: Record<
  BxFormBrand,
  InlineFormBrandConfig
> = {
  capterra: {
    defaultTracking: {
      brand: 'capterra',
      formName: 'DMBUY_2021_CA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2021_CA_Blog_Acq',
      contentRequest: 'Top_Software_Spotlight',
      contentCategory: 'Inline Card',
    },
    termsUrl: 'https://www.capterra.com/legal/terms-of-use/',
    privacyUrl: 'https://www.capterra.com/legal/privacy-policy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'CAP_SEM_form_submission_failed',
  },
  softwareadvice: {
    defaultTracking: {
      brand: 'softwareadvice',
      formName: 'DMBUY_SA_2022_Blog_Acquisition',
      eloquaCampaign: 'DMBUY_SA_2022_Blog_Acquisition',
      contentRequest: 'Product List',
      contentCategory: 'SA_Inline',
    },
    termsUrl: 'https://www.softwareadvice.com/legal-page/general-user-terms/',
    privacyUrl: 'https://www.softwareadvice.com/legal-page/privacy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'SA_forms_submission_failed',
  },
  getapp: {
    defaultTracking: {
      brand: 'getapp',
      formName: 'DMBUY_2022_GA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2022_GA_Blog_Acq',
      contentRequest: 'Product List',
      contentCategory: 'GA_Inline',
    },
    termsUrl: 'https://www.getapp.com/terms_of_use/',
    privacyUrl: 'https://www.getapp.com/privacy_policy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'GA_forms_submission_failed',
  },
};

export const CAPTERRA_BRAND_CONFIG = INLINE_FORM_BRAND_CONFIGS.capterra;
export const SOFTWARE_ADVICE_BRAND_CONFIG =
  INLINE_FORM_BRAND_CONFIGS.softwareadvice;
export const GETAPP_BRAND_CONFIG = INLINE_FORM_BRAND_CONFIGS.getapp;

export function createBrandedInlineForm(
  brandConfig: InlineFormBrandConfig,
  displayName: string
) {
  const BrandedInlineForm = (props: InlineFormProps) => (
    <SharedInlineForm {...props} brandConfig={brandConfig} />
  );

  BrandedInlineForm.displayName = displayName;

  return BrandedInlineForm;
}

export const MODAL_FORM_BRAND_CONFIGS: Record<
  BxFormBrand,
  ModalFormBrandConfig
> = {
  capterra: {
    defaultTracking: {
      brand: 'capterra',
      formName: 'DMBUY_2021_CA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2021_CA_Blog_Acq',
      contentRequest: 'ca-modal',
    },
    termsUrl: 'https://www.capterra.com/legal/terms-of-use/',
    privacyUrl: 'https://www.capterra.com/legal/privacy-policy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'CAP_SEM_form_submission_failed',
  },
  softwareadvice: {
    defaultTracking: {
      brand: 'softwareadvice',
      formName: 'DMBUY_SA_2022_Blog_Acquisition',
      eloquaCampaign: 'DMBUY_SA_2022_Blog_Acquisition',
      contentRequest: 'sa-modal',
    },
    termsUrl: 'https://www.softwareadvice.com/legal-page/general-user-terms/',
    privacyUrl: 'https://www.softwareadvice.com/legal-page/privacy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'SA_forms_submission_failed',
  },
  getapp: {
    defaultTracking: {
      brand: 'getapp',
      formName: 'DMBUY_2022_GA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2022_GA_Blog_Acq',
      contentRequest: 'ga-modal',
    },
    termsUrl: 'https://www.getapp.com/terms_of_use/',
    privacyUrl: 'https://www.getapp.com/privacy_policy/',
    successIcon: <ComparisonChartIcon />,
    submissionFailedMessageKey: 'GA_forms_submission_failed',
  },
};

export const CAPTERRA_MODAL_BRAND_CONFIG = MODAL_FORM_BRAND_CONFIGS.capterra;
export const SOFTWARE_ADVICE_MODAL_BRAND_CONFIG =
  MODAL_FORM_BRAND_CONFIGS.softwareadvice;
export const GETAPP_MODAL_BRAND_CONFIG = MODAL_FORM_BRAND_CONFIGS.getapp;

export function createBrandedModalForm(
  brandConfig: ModalFormBrandConfig,
  displayName: string
) {
  const BrandedModalForm = (props: ModalFormProps) => (
    <SharedModalForm {...props} brandConfig={brandConfig} />
  );

  BrandedModalForm.displayName = displayName;

  return BrandedModalForm;
}

export const VENDOR_CLAIM_PROFILE_FORM_BRAND_CONFIGS: Record<
  BxFormBrand,
  VendorClaimProfileFormBrandConfig
> = {
  capterra: {
    defaultTracking: {
      brand: 'capterra',
      formName: 'CA Vendor Claim Profile',
      eloquaCampaign: 'CA Vendor Claim Profile',
      contentRequest: 'Claim Profile',
      contentCategory: 'CAP_PPL',
    },
    termsUrl: 'https://www.capterra.com/legal/terms-of-use/',
    privacyUrl: 'https://www.capterra.com/legal/privacy-policy/',
    submissionFailedMessageKey: 'CAP_vendor_claim_profile_submission_failed',
  },
  softwareadvice: {
    defaultTracking: {
      brand: 'softwareadvice',
      formName: 'SA Vendor Claim Profile',
      eloquaCampaign: 'SA Vendor Claim Profile',
      contentRequest: 'Claim Profile',
      contentCategory: 'SA_PPL',
    },
    termsUrl: 'https://www.softwareadvice.com/legal-page/general-user-terms/',
    privacyUrl: 'https://www.softwareadvice.com/legal-page/privacy/',
    submissionFailedMessageKey: 'SA_vendor_claim_profile_submission_failed',
  },
  getapp: {
    defaultTracking: {
      brand: 'getapp',
      formName: 'GA Vendor Claim Profile',
      eloquaCampaign: 'GA Vendor Claim Profile',
      contentRequest: 'Claim Profile',
      contentCategory: 'GA_PPL',
    },
    termsUrl: 'https://www.getapp.com/terms_of_use/',
    privacyUrl: 'https://www.getapp.com/privacy_policy/',
    submissionFailedMessageKey: 'GA_vendor_claim_profile_submission_failed',
  },
};

export const CAPTERRA_VENDOR_CLAIM_PROFILE_BRAND_CONFIG =
  VENDOR_CLAIM_PROFILE_FORM_BRAND_CONFIGS.capterra;
export const SOFTWARE_ADVICE_VENDOR_CLAIM_PROFILE_BRAND_CONFIG =
  VENDOR_CLAIM_PROFILE_FORM_BRAND_CONFIGS.softwareadvice;
export const GETAPP_VENDOR_CLAIM_PROFILE_BRAND_CONFIG =
  VENDOR_CLAIM_PROFILE_FORM_BRAND_CONFIGS.getapp;

export function createBrandedVendorClaimProfileForm(
  brandConfig: VendorClaimProfileFormBrandConfig,
  displayName: string
) {
  const BrandedVendorClaimProfileForm = (
    props: VendorClaimProfileFormProps
  ) => <SharedVendorClaimProfileForm {...props} brandConfig={brandConfig} />;

  BrandedVendorClaimProfileForm.displayName = displayName;

  return BrandedVendorClaimProfileForm;
}

export const MULTI_FIELD_INLINE_FORM_BRAND_CONFIGS: Record<
  BxFormBrand,
  MultiFieldInlineFormBrandConfig
> = {
  capterra: {
    defaultTracking: {
      brand: 'capterra',
      formName: 'DMBUY_2021_CA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2021_CA_Blog_Acq',
      contentRequest: 'Multi_Field_Inline',
      contentCategory: 'Multi Field Inline',
    },
    termsUrl: 'https://www.capterra.com/legal/terms-of-use/',
    privacyUrl: 'https://www.capterra.com/legal/privacy-policy/',
    submissionFailedMessageKey: 'CAP_SEM_form_submission_failed',
  },
  softwareadvice: {
    defaultTracking: {
      brand: 'softwareadvice',
      formName: 'DMBUY_SA_2022_Blog_Acquisition',
      eloquaCampaign: 'DMBUY_SA_2022_Blog_Acquisition',
      contentRequest: 'Product List',
      contentCategory: 'SA_Blog_Inline',
    },
    termsUrl: 'https://www.softwareadvice.com/legal-page/general-user-terms/',
    privacyUrl: 'https://www.softwareadvice.com/legal-page/privacy/',
    submissionFailedMessageKey: 'SA_forms_submission_failed',
  },
  getapp: {
    defaultTracking: {
      brand: 'getapp',
      formName: 'DMBUY_2022_GA_Blog_Acq',
      eloquaCampaign: 'DMBUY_2022_GA_Blog_Acq',
      contentRequest: 'Product List',
      contentCategory: 'GA_Blog_Inline',
    },
    termsUrl: 'https://www.getapp.com/terms_of_use/',
    privacyUrl: 'https://www.getapp.com/privacy_policy/',
    submissionFailedMessageKey: 'GA_forms_submission_failed',
  },
};

export const CAPTERRA_MULTI_FIELD_BRAND_CONFIG =
  MULTI_FIELD_INLINE_FORM_BRAND_CONFIGS.capterra;
export const SOFTWARE_ADVICE_MULTI_FIELD_BRAND_CONFIG =
  MULTI_FIELD_INLINE_FORM_BRAND_CONFIGS.softwareadvice;
export const GETAPP_MULTI_FIELD_BRAND_CONFIG =
  MULTI_FIELD_INLINE_FORM_BRAND_CONFIGS.getapp;

export function createBrandedMultiFieldInlineForm(
  brandConfig: MultiFieldInlineFormBrandConfig,
  displayName: string
) {
  const BrandedMultiFieldInlineForm = (props: MultiFieldInlineFormProps) => (
    <SharedMultiFieldInlineForm {...props} brandConfig={brandConfig} />
  );

  BrandedMultiFieldInlineForm.displayName = displayName;

  return BrandedMultiFieldInlineForm;
}
